import React, { useEffect, useMemo, useState } from "react";
import { Download, Phone, Plus, RefreshCw, Search, ShieldCheck, Trash2, Upload, UserCheck } from "lucide-react";
import API from "../services/api";
import AdminLayout from "./AdminLayout";

const emptyImport = { rows: "", file: null, group_id: "", assigned_agent_id: "" };
const emptyLead = { name: "", phone: "", group_id: "", assigned_agent_id: "" };

function AdminLeadCRM() {
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState({});
  const [agents, setAgents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [options, setOptions] = useState([]);
  const [filters, setFilters] = useState({ q: "", status: "all", group_id: "", agent_id: "" });
  const [callFilters, setCallFilters] = useState({ date_from: new Date().toISOString().slice(0, 10), date_to: new Date().toISOString().slice(0, 10), agent_id: "", all_time: false });
  const [importForm, setImportForm] = useState(emptyImport);
  const [leadForm, setLeadForm] = useState(emptyLead);
  const [groupForm, setGroupForm] = useState({ name: "", color: "#2563eb" });
  const [optionForm, setOptionForm] = useState({ label: "", type: "status", color: "#64748b" });
  const [blockForm, setBlockForm] = useState({ phone: "", rows: "", remark: "Never call" });
  const [blocklist, setBlocklist] = useState([]);
  const [callStats, setCallStats] = useState({ rows: [], totals: {} });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const loadStatic = async () => {
    const [agentRes, groupRes, optionRes, blockRes] = await Promise.all([
      API.get("/admin/agents"),
      API.get("/admin/crm-lead-groups"),
      API.get("/admin/crm-remark-options"),
      API.get("/admin/crm-lead-blocklist"),
    ]);
    setAgents(agentRes.data?.agents || []);
    setGroups(Array.isArray(groupRes.data) ? groupRes.data : []);
    setOptions(Array.isArray(optionRes.data) ? optionRes.data : []);
    setBlocklist(Array.isArray(blockRes.data) ? blockRes.data : []);
  };

  const loadLeads = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    const res = await API.get(`/admin/crm-leads${params.toString() ? `?${params.toString()}` : ""}`);
    setLeads(res.data?.rows || []);
    setSummary(res.data?.summary || {});
  };

  const loadCallStats = async () => {
    const params = new URLSearchParams();
    Object.entries(callFilters).forEach(([key, value]) => {
      if (key === "all_time") return;
      if (value) params.set(key, value);
    });
    if (callFilters.all_time) params.set("all_time", "1");
    const res = await API.get(`/admin/crm-call-stats${params.toString() ? `?${params.toString()}` : ""}`);
    setCallStats(res.data || { rows: [], totals: {} });
  };

  useEffect(() => {
    loadStatic().catch(console.log);
  }, []);

  useEffect(() => {
    loadLeads().catch(console.log);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.group_id, filters.agent_id]);

  useEffect(() => {
    loadCallStats().catch(console.log);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callFilters.date_from, callFilters.date_to, callFilters.agent_id]);

  const statusOptions = useMemo(() => options.filter((item) => item.type === "status" || item.type === "category"), [options]);
  const languageOptions = useMemo(() => options.filter((item) => item.type === "language"), [options]);

  const runSearch = () => loadLeads().catch(console.log);

  const createGroup = async () => {
    if (!groupForm.name.trim()) return;
    await API.post("/admin/crm-lead-groups", groupForm);
    setGroupForm({ name: "", color: "#2563eb" });
    await loadStatic();
  };

  const createOption = async () => {
    if (!optionForm.label.trim()) return;
    await API.post("/admin/crm-remark-options", optionForm);
    setOptionForm({ label: "", type: "status", color: "#64748b" });
    await loadStatic();
  };

  const addBlockedNumber = async () => {
    if (!blockForm.phone.trim()) return;
    const res = await API.post("/admin/crm-lead-blocklist", { phone: blockForm.phone, remark: blockForm.remark });
    setBlockForm((current) => ({ ...current, phone: "" }));
    await loadStatic();
    await loadLeads();
    alert(`Blocked number added. Removed ${res.data?.deleted || 0} existing leads.`);
  };

  const importBlockedNumbers = async () => {
    if (!blockForm.rows.trim()) return;
    const res = await API.post("/admin/crm-lead-blocklist/import", { rows: blockForm.rows, remark: blockForm.remark });
    setBlockForm((current) => ({ ...current, rows: "" }));
    await loadStatic();
    await loadLeads();
    alert(`Blocked ${res.data?.blocked || 0} numbers. Removed ${res.data?.deleted || 0} existing leads.`);
  };

  const autoDndScrub = async () => {
    try {
      setBusy(true);
      const res = await API.post("/admin/crm-leads/dnd-scrub", { rows: blockForm.rows });
      if (blockForm.rows.trim()) setBlockForm((current) => ({ ...current, rows: "" }));
      await loadStatic();
      await loadLeads();
      alert(`DND scrub complete. Detected ${res.data?.detected || 0}, unassigned ${res.data?.unassigned || 0}, updated ${res.data?.updated || 0}.`);
    } catch (err) {
      alert(err?.response?.data?.message || "DND scrub failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteBlockedNumber = async (item) => {
    await API.delete(`/admin/crm-lead-blocklist/${item.id}`);
    await loadStatic();
  };

  const createLead = async () => {
    if (!leadForm.phone.trim()) return;
    await API.post("/admin/crm-leads", leadForm);
    setLeadForm(emptyLead);
    await loadLeads();
  };

  const importLeads = async () => {
    try {
      setBusy(true);
      setMessage("");
      const payload = new FormData();
      payload.append("rows", importForm.rows || "");
      payload.append("group_id", importForm.group_id || "");
      payload.append("assigned_agent_id", importForm.assigned_agent_id || "");
      if (importForm.file) payload.append("file", importForm.file);
      const res = await API.post("/admin/crm-leads/import", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportForm(emptyImport);
      setMessage(`Imported ${res.data?.imported || 0} leads. Detected ${res.data?.detected || 0}.`);
      await loadLeads();
    } catch (err) {
      alert(err?.response?.data?.message || "Lead import failed");
    } finally {
      setBusy(false);
    }
  };

  const saveLead = async (lead) => {
    if (lead.source_type === "registered_user") return;
    await API.put(`/admin/crm-leads/${lead.id}`, {
      name: lead.name,
      status: lead.status,
      language: lead.language,
      remark: lead.remark,
      group_id: lead.group_id,
      assigned_agent_id: lead.assigned_agent_id,
    });
    await loadLeads();
  };

  const markCall = async (lead) => {
    if (lead.source_type === "registered_user") return;
    await API.post(`/admin/crm-leads/${lead.id}/call`, {
      status: lead.status || "called",
      remark: lead.remark || "",
    });
    await loadLeads();
  };

  const bulkDeleteFiltered = async () => {
    const label = [
      filters.status && filters.status !== "all" ? `status ${filters.status}` : null,
      filters.group_id ? "selected group" : null,
      filters.agent_id ? "selected agent" : null,
      filters.q ? `search "${filters.q}"` : null,
    ].filter(Boolean).join(", ") || "all visible leads";
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    const res = await API.post("/admin/crm-leads/bulk-delete", filters);
    alert(`Deleted ${res.data?.deleted || 0} leads`);
    await loadLeads();
  };

  const deleteLead = async (lead) => {
    if (lead.source_type === "registered_user") return;
    if (!window.confirm(`Delete ${lead.phone}?`)) return;
    await API.delete(`/admin/crm-leads/${lead.id}`);
    await loadLeads();
  };

  const downloadAllNumbers = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    const res = await API.get(`/admin/crm-leads/export${params.toString() ? `?${params.toString()}` : ""}`, {
      responseType: "blob",
    });
    const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `lead-numbers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const inferFromRemark = (value) => {
    const text = String(value || "").trim().toLowerCase();
    const patch = {};
    if (!text) return patch;
    for (const option of options) {
      const label = String(option.label || "").trim();
      const normalizedLabel = label.toLowerCase();
      if (!normalizedLabel) continue;
      if (text === normalizedLabel || text.includes(normalizedLabel)) {
        if (option.type === "language") patch.language = label;
        if (option.type === "status" || option.type === "category") patch.status = normalizedLabel;
      }
    }
    return patch;
  };

  const updateLead = (id, patch) => {
    setLeads((current) => current.map((lead) => (lead.id === id ? { ...lead, ...patch } : lead)));
  };

  const handleRemarkChange = (id, value) => {
    updateLead(id, { remark: value, ...inferFromRemark(value) });
  };

  const handleCallClick = (lead) => {
    if (lead.source_type === "registered_user") return;
    updateLead(lead.id, { status: "called" });
    markCall({ ...lead, status: "called" }).catch(console.log);
  };

  return (
    <AdminLayout>
      <section style={page}>
        <style>{css}</style>
        <div style={header}>
          <div>
            <h1 style={title}>Lead Calling CRM</h1>
            <p style={subtitle}>Upload phone/name lists, assign them to agents, and track who called whom.</p>
          </div>
          <button type="button" style={refreshButton} onClick={() => { loadStatic(); loadLeads(); }}>
            <RefreshCw size={16} /> Refresh
          </button>
        </div>

        <section style={card}>
          <div style={toolbar}>
            <h2 style={{ ...sectionTitle, marginRight: "auto" }}><Phone size={18} /> Agent Call Totals</h2>
            <button type="button" style={callFilters.all_time ? activeSmallButton : smallButton} onClick={() => setCallFilters((c) => ({ ...c, all_time: !c.all_time }))}>
              All Time
            </button>
            <input style={filterInput} type="date" value={callFilters.date_from} disabled={callFilters.all_time} onChange={(e) => setCallFilters((c) => ({ ...c, date_from: e.target.value }))} />
            <input style={filterInput} type="date" value={callFilters.date_to} disabled={callFilters.all_time} onChange={(e) => setCallFilters((c) => ({ ...c, date_to: e.target.value }))} />
            <select style={filterInput} value={callFilters.agent_id} onChange={(e) => setCallFilters((c) => ({ ...c, agent_id: e.target.value }))}>
              <option value="">All agents</option>
              {agents.map((agent) => <option key={agent.id} value={agent.id}>{agent.name}</option>)}
            </select>
            <button type="button" style={smallButton} onClick={loadCallStats}>Apply</button>
          </div>
          <div style={callStatsGrid}>
            <Stat label="Calls" value={callStats.totals?.total_calls || 0} tone="#d97706" />
            <Stat label="Unique Numbers" value={callStats.totals?.unique_leads || 0} tone="#2563eb" />
            <Stat label="Registered Users" value={callStats.totals?.registered_users || 0} tone="#7c3aed" />
            <Stat label="Interested" value={callStats.totals?.interested_count || 0} tone="#16a34a" />
          </div>
          <div style={miniStatsList}>
            {(callStats.rows || []).map((row, index) => (
              <div key={`${row.call_date}-${row.agent_id || index}`} style={miniStatsRow}>
                <strong>{row.agent_name || "Admin"}</strong>
                <span>{String(row.call_date || "").slice(0, 10)}</span>
                <b>{row.total_calls} calls</b>
                <span>{row.unique_leads} numbers</span>
              </div>
            ))}
            {!callStats.rows?.length ? <p style={emptyText}>No calls in selected date.</p> : null}
          </div>
        </section>

        <div style={statsGrid}>
          <Stat label="Total Leads" value={summary.total || 0} />
          <Stat label="Assigned" value={summary.assigned || 0} tone="#2563eb" />
          <Stat label="Existing Users" value={summary.existing_users || 0} tone="#7c3aed" />
          <Stat label="DND" value={summary.status_counts?.dnd || 0} tone="#dc2626" onClick={() => setFilters((current) => ({ ...current, status: "dnd" }))} />
          <Stat label="Called" value={summary.called || 0} tone="#16a34a" />
        </div>

        <div style={topGrid}>
          <section style={card}>
            <h2 style={sectionTitle}><Upload size={18} /> Upload Leads</h2>
            <div style={fieldGrid}>
              <select style={input} value={importForm.group_id} onChange={(e) => setImportForm((c) => ({ ...c, group_id: e.target.value }))}>
                <option value="">No group</option>
                {groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
              </select>
              <select style={input} value={importForm.assigned_agent_id} onChange={(e) => setImportForm((c) => ({ ...c, assigned_agent_id: e.target.value }))}>
                <option value="">No agent</option>
                {agents.map((agent) => <option key={agent.id} value={agent.id}>{agent.name}</option>)}
              </select>
            </div>
            <label style={fileDrop}>
              <Download size={18} />
              {importForm.file ? importForm.file.name : "Upload CSV / TXT / HTML export"}
              <input
                type="file"
                accept=".csv,.txt,.html,.htm,.xls,.xlsx"
                style={{ display: "none" }}
                onChange={(e) => setImportForm((c) => ({ ...c, file: e.target.files?.[0] || null }))}
              />
            </label>
            <textarea
              style={textarea}
              placeholder={"Or paste rows here:\nRahul, 9876543210\nAmit 9123456789"}
              value={importForm.rows}
              onChange={(e) => setImportForm((c) => ({ ...c, rows: e.target.value }))}
            />
            <button type="button" style={primaryButton} onClick={importLeads} disabled={busy || (!importForm.file && !importForm.rows.trim())}>
              {busy ? "Importing..." : "Import Leads"}
            </button>
            {message ? <p style={successText}>{message}</p> : null}
          </section>

          <section style={card}>
            <h2 style={sectionTitle}><Plus size={18} /> Quick Setup</h2>
            <div style={miniForm}>
              <input style={input} placeholder="New group name" value={groupForm.name} onChange={(e) => setGroupForm((c) => ({ ...c, name: e.target.value }))} />
              <input style={colorInput} type="color" value={groupForm.color} onChange={(e) => setGroupForm((c) => ({ ...c, color: e.target.value }))} />
              <button type="button" style={smallButton} onClick={createGroup}>Add Group</button>
            </div>
            <div style={miniForm}>
              <input style={input} placeholder="Remark/language option" value={optionForm.label} onChange={(e) => setOptionForm((c) => ({ ...c, label: e.target.value }))} />
              <select style={input} value={optionForm.type} onChange={(e) => setOptionForm((c) => ({ ...c, type: e.target.value }))}>
                <option value="status">Status</option>
                <option value="language">Language</option>
                <option value="category">Category</option>
              </select>
              <button type="button" style={smallButton} onClick={createOption}>Add Option</button>
            </div>
            <div style={{ ...miniForm, gridTemplateColumns: "1fr 1fr" }}>
              <input style={input} placeholder="Name" value={leadForm.name} onChange={(e) => setLeadForm((c) => ({ ...c, name: e.target.value }))} />
              <input style={input} placeholder="Phone" value={leadForm.phone} onChange={(e) => setLeadForm((c) => ({ ...c, phone: e.target.value.replace(/\D/g, "").slice(0, 12) }))} />
              <select style={input} value={leadForm.group_id} onChange={(e) => setLeadForm((c) => ({ ...c, group_id: e.target.value }))}>
                <option value="">Group</option>
                {groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
              </select>
              <select style={input} value={leadForm.assigned_agent_id} onChange={(e) => setLeadForm((c) => ({ ...c, assigned_agent_id: e.target.value }))}>
                <option value="">Agent</option>
                {agents.map((agent) => <option key={agent.id} value={agent.id}>{agent.name}</option>)}
              </select>
              <button type="button" style={{ ...smallButton, gridColumn: "1 / -1" }} onClick={createLead}>Add Single Lead</button>
            </div>
          </section>

          <section style={card}>
            <h2 style={sectionTitle}><Trash2 size={18} /> Never Call Blocklist</h2>
            <div style={miniForm}>
              <input style={input} placeholder="Phone number" value={blockForm.phone} onChange={(e) => setBlockForm((c) => ({ ...c, phone: e.target.value.replace(/\D/g, "").slice(0, 12) }))} />
              <input style={input} placeholder="Remark" value={blockForm.remark} onChange={(e) => setBlockForm((c) => ({ ...c, remark: e.target.value }))} />
              <button type="button" style={dangerToolbarButton} onClick={addBlockedNumber}>Block</button>
            </div>
            <textarea style={{ ...textarea, minHeight: 74 }} placeholder="Paste numbers to block, one per line" value={blockForm.rows} onChange={(e) => setBlockForm((c) => ({ ...c, rows: e.target.value }))} />
            <div style={dndActionRow}>
              <button type="button" style={dangerToolbarButton} onClick={importBlockedNumbers}>Import Blocklist</button>
              <button type="button" style={scrubButton} onClick={autoDndScrub} disabled={busy}>
                <ShieldCheck size={15} />
                {busy ? "Scrubbing..." : "Auto DND Scrub"}
              </button>
            </div>
            <div style={blocklistBox}>
              {blocklist.slice(0, 8).map((item) => (
                <span key={item.id} style={blockChip}>
                  {item.normalized_phone}
                  <button type="button" onClick={() => deleteBlockedNumber(item)}>x</button>
                </span>
              ))}
              {!blocklist.length ? <span style={muted}>No blocked numbers.</span> : null}
            </div>
          </section>
        </div>

        <section style={card}>
          <div style={toolbar}>
            <div style={searchBox}>
              <Search size={16} />
              <input value={filters.q} onChange={(e) => setFilters((c) => ({ ...c, q: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && runSearch()} placeholder="Search name, phone, remark" />
            </div>
            <select style={filterInput} value={filters.status} onChange={(e) => setFilters((c) => ({ ...c, status: e.target.value }))}>
              <option value="all">All status</option>
              <option value="registered_users">Registered Users</option>
              {statusOptions.map((item) => <option key={item.id} value={item.label.toLowerCase()}>{item.label}</option>)}
            </select>
            <select style={filterInput} value={filters.group_id} onChange={(e) => setFilters((c) => ({ ...c, group_id: e.target.value }))}>
              <option value="">All groups</option>
              {groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
            </select>
            <select style={filterInput} value={filters.agent_id} onChange={(e) => setFilters((c) => ({ ...c, agent_id: e.target.value }))}>
              <option value="">All agents</option>
              {agents.map((agent) => <option key={agent.id} value={agent.id}>{agent.name}</option>)}
            </select>
            <button type="button" style={smallButton} onClick={runSearch}>Search</button>
            <button type="button" style={exportButton} onClick={downloadAllNumbers}><Download size={15} /> Download Numbers</button>
            <button type="button" style={dangerToolbarButton} onClick={bulkDeleteFiltered}><Trash2 size={15} /> Delete Filtered</button>
          </div>

          <div style={tableWrap}>
            <table style={table} className="lead-table">
              <thead>
                <tr>
                  <th>Lead</th>
                  <th>Call</th>
                  <th>Agent / Group</th>
                  <th>Status</th>
                  <th>Language</th>
                  <th>Remark</th>
                  <th>Matched User</th>
                  <th>Last Call</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.id} style={isCalledLead(lead) ? calledRow : null}>
                    <td>
                      <input style={plainInput} value={lead.name || ""} disabled={lead.source_type === "registered_user"} onChange={(e) => updateLead(lead.id, { name: e.target.value })} />
                      <div style={phoneText}>{lead.phone}</div>
                      {lead.source_type === "registered_user" ? <div style={registeredBadge}>Registered user</div> : null}
                      {isDndLead(lead) ? <div style={dndBadge}>DND - not assigned</div> : null}
                    </td>
                    <td>
                      {isDndLead(lead) ? (
                        <span style={dndCallButton}><Phone size={15} /> DND</span>
                      ) : (
                        <a style={isCalledLead(lead) ? calledButton : callButton} href={`tel:${lead.normalized_phone || lead.phone}`} onClick={() => handleCallClick(lead)}>
                          <Phone size={15} /> Dial
                        </a>
                      )}
                    </td>
                    <td>
                      <select style={plainSelect} value={lead.assigned_agent_id || ""} disabled={lead.source_type === "registered_user" || isDndLead(lead)} onChange={(e) => updateLead(lead.id, { assigned_agent_id: e.target.value })}>
                        <option value="">No agent</option>
                        {agents.map((agent) => <option key={agent.id} value={agent.id}>{agent.name}</option>)}
                      </select>
                      <select style={plainSelect} value={lead.group_id || ""} disabled={lead.source_type === "registered_user"} onChange={(e) => updateLead(lead.id, { group_id: e.target.value })}>
                        <option value="">No group</option>
                        {groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
                      </select>
                    </td>
                    <td>
                      <select style={plainSelect} value={lead.status || "new"} disabled={lead.source_type === "registered_user"} onChange={(e) => updateLead(lead.id, { status: e.target.value })}>
                        <option value="new">New</option>
                        {statusOptions.map((item) => <option key={item.id} value={item.label.toLowerCase()}>{item.label}</option>)}
                      </select>
                    </td>
                    <td>
                      <select style={plainSelect} value={lead.language || ""} disabled={lead.source_type === "registered_user"} onChange={(e) => updateLead(lead.id, { language: e.target.value })}>
                        <option value="">-</option>
                        {languageOptions.map((item) => <option key={item.id} value={item.label}>{item.label}</option>)}
                      </select>
                    </td>
                    <td><input style={plainInput} value={lead.remark || ""} disabled={lead.source_type === "registered_user"} onChange={(e) => handleRemarkChange(lead.id, e.target.value)} placeholder="Remark" /></td>
                    <td>
                      {lead.matched_user_id ? (
                        <a style={userLink} href={`/admin/users/${lead.matched_user_id}`}>#{lead.matched_user_id} {lead.matched_user_name}</a>
                      ) : <span style={muted}>No ID</span>}
                      <div style={muted}>{lead.location_hint || "Location unknown"}</div>
                    </td>
                    <td>
                      <strong>{lead.call_count || 0} calls</strong>
                      <div style={muted}>{formatDateTime(lead.last_called_at)}</div>
                      <div style={muted}>{lead.last_called_by_agent_name || ""}</div>
                    </td>
                    <td>
                      <div style={actionStack}>
                        {lead.source_type === "registered_user" ? (
                          <span style={registeredNote}>Dial or open profile</span>
                        ) : (
                          <>
                            <button type="button" style={saveButton} onClick={() => saveLead(lead)}>Save</button>
                            <button type="button" style={callLogButton} onClick={() => markCall(lead)}><UserCheck size={14} /> Log Call</button>
                            <button type="button" style={deleteButton} onClick={() => deleteLead(lead)}><Trash2 size={14} /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!leads.length ? <p style={emptyText}>No leads found.</p> : null}
          </div>
        </section>
      </section>
    </AdminLayout>
  );
}

function Stat({ label, value, tone = "#0f172a", onClick }) {
  return (
    <article style={onClick ? clickableStatCard : statCard} onClick={onClick}>
      <span>{label}</span>
      <strong style={{ color: tone }}>{Number(value || 0).toLocaleString("en-IN")}</strong>
    </article>
  );
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function isCalledLead(lead) {
  return String(lead?.status || "").toLowerCase() === "called";
}

function isDndLead(lead) {
  const status = String(lead?.status || "").toLowerCase();
  const remark = String(lead?.remark || "").toLowerCase();
  return status === "dnd" || /\bdnd\b/.test(remark) || remark.includes("do not disturb") || remark.includes("never call");
}

const page = { display: "grid", gap: 18, color: "#0f172a" };
const header = { display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start", flexWrap: "wrap" };
const title = { margin: 0, fontSize: 30, fontWeight: 950, letterSpacing: "-0.04em" };
const subtitle = { margin: "6px 0 0", color: "#64748b" };
const statsGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 };
const callStatsGrid = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10 };
const statCard = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 18, padding: 18, display: "grid", gap: 8, boxShadow: "0 10px 24px rgba(15,23,42,.05)" };
const clickableStatCard = { ...statCard, cursor: "pointer" };
const topGrid = { display: "grid", gridTemplateColumns: "1.1fr .9fr", gap: 16, alignItems: "start" };
const card = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 22, padding: 18, boxShadow: "0 14px 34px rgba(15,23,42,.06)", display: "grid", gap: 14 };
const sectionTitle = { margin: 0, display: "flex", alignItems: "center", gap: 8, fontSize: 18 };
const fieldGrid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 };
const input = { minHeight: 44, border: "1px solid #dbe4f0", borderRadius: 12, padding: "0 12px", outline: "none", background: "#fff", color: "#0f172a" };
const filterInput = { ...input, minWidth: 150 };
const colorInput = { width: 48, minHeight: 44, border: "1px solid #dbe4f0", borderRadius: 12, background: "#fff" };
const textarea = { minHeight: 108, border: "1px solid #dbe4f0", borderRadius: 14, padding: 12, resize: "vertical", font: "inherit", outline: "none" };
const fileDrop = { minHeight: 58, border: "1px dashed #93c5fd", background: "#eff6ff", color: "#2563eb", borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 10, fontWeight: 900, cursor: "pointer" };
const primaryButton = { minHeight: 48, border: 0, borderRadius: 14, background: "linear-gradient(135deg,#2563eb,#7c3aed)", color: "#fff", fontWeight: 950, cursor: "pointer" };
const smallButton = { minHeight: 42, border: 0, borderRadius: 12, background: "#111827", color: "#fff", fontWeight: 900, padding: "0 14px", cursor: "pointer" };
const activeSmallButton = { ...smallButton, background: "linear-gradient(135deg,#2563eb,#7c3aed)" };
const exportButton = { minHeight: 42, border: 0, borderRadius: 12, background: "linear-gradient(135deg,#059669,#16a34a)", color: "#fff", fontWeight: 900, padding: "0 14px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 7 };
const dangerToolbarButton = { minHeight: 42, border: 0, borderRadius: 12, background: "#fee2e2", color: "#b91c1c", fontWeight: 900, padding: "0 14px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 7 };
const scrubButton = { minHeight: 42, border: 0, borderRadius: 12, background: "#111827", color: "#fff", fontWeight: 900, padding: "0 14px", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7 };
const refreshButton = { ...smallButton, display: "inline-flex", alignItems: "center", gap: 8, background: "#f8fafc", color: "#0f172a", border: "1px solid #e5e7eb" };
const miniForm = { display: "grid", gridTemplateColumns: "1fr auto auto", gap: 10, alignItems: "center" };
const dndActionRow = { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" };
const toolbar = { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" };
const searchBox = { minHeight: 44, border: "1px solid #dbe4f0", borderRadius: 12, padding: "0 12px", display: "flex", alignItems: "center", gap: 8, flex: "1 1 240px" };
const tableWrap = { overflowX: "auto", border: "1px solid #eef2f7", borderRadius: 16 };
const table = { width: "100%", borderCollapse: "collapse", minWidth: 960 };
const plainInput = { width: "100%", minHeight: 36, border: "1px solid #e5e7eb", borderRadius: 10, padding: "0 10px", outline: "none" };
const plainSelect = { ...plainInput, marginBottom: 6, background: "#fff" };
const phoneText = { color: "#64748b", fontSize: 12, marginTop: 5, fontWeight: 800 };
const registeredBadge = { display: "inline-flex", marginTop: 6, padding: "4px 8px", borderRadius: 999, background: "#eff6ff", color: "#1d4ed8", fontSize: 11, fontWeight: 900 };
const dndBadge = { display: "inline-flex", marginTop: 6, padding: "4px 8px", borderRadius: 999, background: "#fee2e2", color: "#b91c1c", fontSize: 11, fontWeight: 900 };
const registeredNote = { color: "#1d4ed8", background: "#eff6ff", borderRadius: 10, padding: "8px 10px", fontSize: 12, fontWeight: 900, textAlign: "center" };
const callButton = { minHeight: 38, padding: "0 12px", borderRadius: 999, background: "#dcfce7", color: "#15803d", display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none", fontWeight: 950 };
const calledButton = { ...callButton, background: "linear-gradient(135deg,#fde68a,#f59e0b)", color: "#78350f", boxShadow: "0 8px 18px rgba(217,119,6,.22)" };
const dndCallButton = { ...callButton, background: "#fee2e2", color: "#b91c1c", cursor: "not-allowed" };
const calledRow = { background: "linear-gradient(90deg, rgba(253,230,138,.35), rgba(255,255,255,0))" };
const saveButton = { minHeight: 34, border: 0, borderRadius: 10, background: "#2563eb", color: "#fff", fontWeight: 900, padding: "0 10px" };
const callLogButton = { minHeight: 34, border: 0, borderRadius: 10, background: "#ecfdf5", color: "#047857", fontWeight: 900, padding: "0 10px", display: "inline-flex", alignItems: "center", gap: 5 };
const deleteButton = { minHeight: 34, border: 0, borderRadius: 10, background: "#fee2e2", color: "#dc2626", fontWeight: 900, padding: "0 10px" };
const actionStack = { display: "flex", gap: 6, flexWrap: "wrap" };
const muted = { color: "#64748b", fontSize: 12 };
const userLink = { color: "#2563eb", fontWeight: 900, textDecoration: "none" };
const emptyText = { padding: 18, color: "#64748b" };
const successText = { margin: 0, color: "#15803d", fontWeight: 800 };
const miniStatsList = { display: "grid", gap: 8 };
const miniStatsRow = { minHeight: 42, border: "1px solid #eef2f7", borderRadius: 12, padding: "8px 12px", display: "grid", gridTemplateColumns: "1.2fr 1fr auto auto", gap: 10, alignItems: "center", color: "#475569" };
const blocklistBox = { display: "flex", gap: 8, flexWrap: "wrap", maxHeight: 92, overflowY: "auto" };
const blockChip = { minHeight: 34, borderRadius: 999, background: "#fff1f2", border: "1px solid #fecdd3", color: "#be123c", display: "inline-flex", alignItems: "center", gap: 8, padding: "0 10px", fontWeight: 900 };
const css = `
  .lead-table th {
    text-align: left;
    padding: 13px 12px;
    color: #64748b;
    font-size: 12px;
    text-transform: uppercase;
    background: #f8fafc;
    border-bottom: 1px solid #eef2f7;
  }
  .lead-table td {
    padding: 12px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: top;
  }
  @media (max-width: 900px) {
    .lead-table {
      min-width: 980px !important;
    }
  }
  @media (max-width: 760px) {
    section {
      min-width: 0;
    }
    .lead-table {
      min-width: 900px !important;
    }
  }
`;

export default AdminLeadCRM;
